import { CALCULATION_RESULT_TEMPLATE, STATE_TEMPLATE } from '../../../../config/templates';
import { field } from '../../../../field-types';
import { defineRepresentation, RepresentationCategory, RepresentationId, TagId } from '../../../../ontology.model';

// ------------------------------------------------
// MODEL DEFINITIONS
// ------------------------------------------------

const ArithmeticParamsModel = {
  u1: field.number({
    label: 'u_1',
    label_type: 'latex',
    description: 'The first term of the sequence ($u_1$)',
    example: 1,
  }),
  d: field.number({
    label: 'd',
    label_type: 'latex',
    description: 'The common difference between consecutive terms ($d = u_{n+1} - u_n$)',
    example: 3,
  }),
  n: field.natural({
    label: 'n',
    label_type: 'latex',
    description: 'The position of the term ($n \\geq 1$)',
    example: 10,
  }),
} as const;

const NthTermModel = {
  value: field.number({
    label: 'u_n',
    label_type: 'latex',
    description: 'The value of the $n$-th term',
    example: 28,
  }),
  notation: field.latex({
    description: 'The algebraic notation (e.g. $u_{10}$)',
    example: 'u_{10}',
    optional: true,
  }),
  expression: field.latex({
    description: 'The mathematical expression with substituted values',
    example: '1 + (10-1)(3)',
    optional: true,
  }),
} as const;

const PartialSumModel = {
  value: field.number({
    label: 'S_n',
    label_type: 'latex',
    description: 'The sum of the first $n$ terms',
    example: 145,
  }),
  notation: field.latex({
    description: 'The algebraic notation (e.g. $S_{10}$)',
    example: 'S_{10}',
    optional: true,
  }),
  expression: field.latex({
    description: 'The mathematical expression with substituted values',
    example: '\\frac{10}{2}(2(1) + (10-1)(3))',
    optional: true,
  }),
} as const;

const SequenceBehaviorModel = {
  state: field.enum(['INCREASING', 'DECREASING', 'CONSTANT'] as const, {
    description: 'The growth pattern of the sequence terms',
    example: 'INCREASING',
  }),
  description: field.string({
    description: 'A human-readable explanation of the behavior',
    example: 'Terms increase by 3 each step',
    optional: true,
  }),
} as const;

// ------------------------------------------------
// MODEL REGISTRY & ID CONSTANTS
// ------------------------------------------------
// Define IDs first so they can be used in both representations and procedures.
// Keys must match representation IDs exactly for type inference to work.

const REP_IDS = {
  params: 'rep.arithmetic_sequence_params' satisfies RepresentationId,
  nthTerm: 'rep.arithmetic_nth_term' satisfies RepresentationId,
  partialSum: 'rep.arithmetic_partial_sum' satisfies RepresentationId,
  monotonicity: 'rep.arithmetic_monotonicity' satisfies RepresentationId,
} as const;

const TAG_IDS = {
  arithmetic: 'tag.arith' satisfies TagId,
  sequences: 'tag.arith.seq' satisfies TagId,
  series: 'tag.arith.series' satisfies TagId,
} as const;

// ------------------------------------------------
// REPRESENTATIONS
// ------------------------------------------------

export const rep = {
  params: defineRepresentation({
    id: REP_IDS.params,
    name: 'Sequence Definition',
    tag_ids: [TAG_IDS.arithmetic],
    description:
      'The defining parameters of an arithmetic sequence: first term $u_1$, common difference $d$, and term position $n$.',
    category: RepresentationCategory.Object,
    type: 'numerical',
    model: ArithmeticParamsModel,
    template: 'u₁={{u1}}, d={{d}} (n={{n}})',
    example_data: { u1: 1, d: 3, n: 10 },
  }),

  term_result: defineRepresentation({
    id: REP_IDS.nthTerm,
    name: 'N-th Term',
    tag_ids: [TAG_IDS.sequences],
    description: 'The computed value of the $n$-th term in the sequence ($u_n = u_1 + (n-1)d$).',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: NthTermModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      value: 28,
      notation: 'u_{10}',
      expression: '1 + (10-1)(3)',
    },
  }),

  partial_sum_result: defineRepresentation({
    id: REP_IDS.partialSum,
    name: 'Partial Sum',
    tag_ids: [TAG_IDS.series],
    description: 'The sum of the first $n$ terms of the sequence ($S_n$).',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: PartialSumModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      value: 145,
      notation: 'S_{10}',
      expression: '\\frac{10}{2}(2(1) + (10-1)(3))',
    },
  }),

  behavior: defineRepresentation({
    id: REP_IDS.monotonicity,
    name: 'Monotonicity',
    tag_ids: [TAG_IDS.sequences],
    description:
      'The growth pattern of the sequence: whether terms are increasing, decreasing, or constant based on the sign of $d$.',
    category: RepresentationCategory.State,
    type: 'state',
    model: SequenceBehaviorModel,
    template: STATE_TEMPLATE,
    example_data: { state: 'INCREASING', description: 'Terms increase by 3 each step.' },
  }),
};

// Export as array for the compiler
export const arithmeticRepresentations = Object.values(rep);

export const models = {
  [REP_IDS.params]: ArithmeticParamsModel,
  [REP_IDS.nthTerm]: NthTermModel,
  [REP_IDS.partialSum]: PartialSumModel,
  [REP_IDS.monotonicity]: SequenceBehaviorModel,
} as const;

export { REP_IDS };
